/**
 * Generator 5: Table component — (authenticated)/<n>/<n>-table.tsx
 * Generates data table with server-side pagination, sorting, and row selection
 */

import type { GeneratedFile, GeneratorOptions, Schema } from '@core-engine/schema/types.js'
import { createGeneratedFile } from '@core-engine/utils/generated-files.js'
import {
  pluralize,
  singularize,
  toCamelCase,
  toKebabCase,
  toPascalCase
} from '@core-engine/utils/string.js'
import { resolveSchemaActionImportPath } from '@next-adapter/config/paths.js'
import { resolveEntityFilters } from '@next-adapter/generators/entity-filters.js'

// ============================================================================
// Public API
// ============================================================================

export interface TableGeneratorResult {
  files: GeneratedFile[]
}

/**
 * Generate table component for entity list view
 */
export function generateTable(
  schema: Schema,
  pagesDir: string,
  options: GeneratorOptions = {}
): TableGeneratorResult {
  const actionImportPath = resolveSchemaActionImportPath(schema.name, options.outputNamespace)
  const singular = singularize(schema.name)
  const plural = pluralize(schema.name)
  const Singular = toPascalCase(singular)
  const Plural = toPascalCase(plural)
  const camelPlural = toCamelCase(plural)
  const tableFileName = `${toKebabCase(plural)}-table.tsx`
  const adminRoutePath = options.adminRoutePath ?? '/admin'
  const resolvedFilters = resolveEntityFilters(schema).map((filter) => ({
    ...filter,
    variableName: toCamelCase(filter.key),
    fromVariableName: `${toCamelCase(filter.key)}From`,
    toVariableName: `${toCamelCase(filter.key)}To`
  }))
  const filterProps = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [`${filter.fromVariableName}?: string`, `${filter.toVariableName}?: string`]
        : [`${filter.variableName}?: string`]
    )
    .join('\n  ')
  const filterParams = resolvedFilters
    .flatMap((filter) =>
      filter.type === 'date-range'
        ? [filter.fromVariableName, filter.toVariableName]
        : [filter.variableName]
    )
    .join(', ')
  const hasEdit = schema.actions?.edit ?? false

  const content = `'use client'

import {
  DataTablePagination,
} from '@admin/components/shared/data-table/data-table-pagination'
import {
  DataGrid,
  DataGridBody,
  DataGridCell,
  DataGridHead,
  DataGridHeader,
  DataGridRow
} from '@admin/components/shared/data-table/data-grid'
import {
  type ColumnDef,
  flexRender,
  getCoreRowModel,
  useReactTable
} from '@tanstack/react-table'
import { GripVertical } from 'lucide-react'
import { parseAsInteger, parseAsString, useQueryStates } from 'nuqs'
${hasEdit ? "import { useRouter } from 'next/navigation'\n" : ''}import { use${Plural} } from '@admin/hooks/use-${schema.name}'
import type { ${Singular} } from '@admin/actions/${actionImportPath}'
import { Card, CardContent } from '@admin/components/ui/card'
import { useTableUtils } from '@admin/hooks/use-table-utils'
import { cn } from '@admin/utils/shared/cn'

interface ${Plural}TableProps<TValue> extends React.HTMLAttributes<HTMLDivElement> {
  columns: ColumnDef<${Singular}, TValue>[]
  selectedIds: string[]
  setSelectedIds: (ids: string[]) => void
  search?: string
  ${filterProps}
}

export function ${Plural}Table<TValue>({ columns, selectedIds, setSelectedIds, search, ${filterParams}, className }: ${Plural}TableProps<TValue>) {
  const defaultPageSize = 20
${hasEdit ? '  const router = useRouter()\n' : ''}  const [{
    page: pageIndex,
    size: pageSize,
    sort: sortField,
    dir: sortDir
  }, setTableQuery] = useQueryStates({
    page: parseAsInteger.withDefault(0),
    size: parseAsInteger.withDefault(defaultPageSize),
    sort: parseAsString.withDefault(''),
    dir: parseAsString.withDefault('')
  })

  const safePageIndex = Number.isFinite(pageIndex) && pageIndex > 0 ? pageIndex : 0
  const safePageSize = pageSize === -1 || (Number.isFinite(pageSize) && pageSize > 0) ? pageSize : defaultPageSize
  const { data, error, isPending } = use${Plural}(search, ${filterParams}, safePageIndex, safePageSize, sortField || undefined, (sortDir as 'asc' | 'desc') || undefined)

  const tableData = data?.${camelPlural} ?? []
  const totalRows = data?.total ?? 0
  const effectivePageSize = safePageSize === -1 ? totalRows : safePageSize
  const pageCount = effectivePageSize > 0 ? Math.ceil(totalRows / effectivePageSize) : 1
  const shouldShowPagination = totalRows > defaultPageSize

  const {
    columnSizing,
    handleColumnSizingChange,
    handlePaginationChange,
    handleRowSelectionChange,
    handleSortingChange,
    hoveredColumnId,
    registerTable,
    resetColumnSizingToContainer,
    rowSelection,
    setHoveredColumnId,
    sorting,
    tableContainerRef
  } = useTableUtils<${Singular}>({
    data: tableData,
    selectedIds,
    onSelectedIdsChange: setSelectedIds,
    pageIndex: safePageIndex,
    pageSize: effectivePageSize,
    setPageIndex: (nextPageIndex) => setTableQuery({ page: nextPageIndex }),
    setSortingParams: ({
      pageIndex: nextPageIndex,
      sortDir: nextSortDir,
      sortField: nextSortField
    }) => setTableQuery({
      page: nextPageIndex,
      sort: nextSortField,
      dir: nextSortDir
    }),
    sortField,
    sortDir
  })

  const table = useReactTable({
    data: tableData,
    columns,
    columnResizeMode: 'onChange',
    defaultColumn: {
      minSize: 48,
      size: 220
    },
    getCoreRowModel: getCoreRowModel(),
    manualPagination: true,
    manualSorting: true,
    pageCount,
    rowCount: totalRows,
    onSortingChange: handleSortingChange,
    onColumnSizingChange: handleColumnSizingChange,
    onRowSelectionChange: handleRowSelectionChange,
    onPaginationChange: handlePaginationChange,
    state: {
      sorting,
      columnSizing,
      rowSelection,
      pagination: {
        pageIndex: safePageIndex,
        pageSize: effectivePageSize
      }
    }
  })
  registerTable(table)
  const gridTemplateColumns = table
    .getVisibleLeafColumns()
    .map((column) => \`\${column.getSize()}px\`)
    .join(' ')

  return (
    <div
      className={cn("flex flex-col gap-6 pt-px pb-2 h-full", className)}
    >
      <Card className="min-h-0 flex-1 p-0">
        <CardContent ref={tableContainerRef} className="flex min-h-0 flex-1 flex-col p-0">
          <DataGrid containerClassName="min-h-0 flex-1" gridTemplateColumns={gridTemplateColumns} style={{ width: table.getTotalSize() }}>
            <DataGridHeader>
              {table.getHeaderGroups().map((headerGroup) => (
                <DataGridRow key={headerGroup.id}>
                  {headerGroup.headers.map((header, headerIndex) => {
                    const meta = header.column.columnDef.meta as Record<string, unknown> | undefined
                    const resizeLabel = typeof header.column.columnDef.header === 'string' ? header.column.columnDef.header : header.column.id
                    const nextHeader = headerGroup.headers[headerIndex + 1]
                    const canResizeColumn = header.column.getCanResize() && nextHeader?.column.getCanResize() === true
                    return (
                      <DataGridHead
                        key={header.id}
                        className={cn('group/resize-header relative overflow-hidden text-foreground', {
                          'text-center': meta?.align === 'center',
                          'text-right': meta?.align === 'right'
                        })}
                        data-column-id={header.column.id}
                        onMouseEnter={() => setHoveredColumnId(header.column.id)}
                        onMouseLeave={() => setHoveredColumnId((current) => (current === header.column.id ? null : current))}
                      >
                        {header.isPlaceholder
                          ? null
                          : flexRender(header.column.columnDef.header, header.getContext())}
                        {canResizeColumn ? (
                          <button
                            type="button"
                            aria-label={\`Resize \${resizeLabel} column\`}
                            className="group/resize-handle absolute right-0 top-0 z-10 flex h-full w-5 cursor-col-resize touch-none select-none items-center justify-center text-muted-foreground opacity-0 transition-opacity hover:text-foreground focus-visible:text-foreground focus-visible:opacity-100 focus-visible:outline-none group-focus-within/resize-header:opacity-100 data-[column-hovered=true]:opacity-100 data-[resizing=true]:opacity-100"
                            data-resize-handle
                            data-column-hovered={hoveredColumnId === header.column.id ? 'true' : 'false'}
                            data-resizing={header.column.getIsResizing() ? 'true' : 'false'}
                            onDoubleClick={resetColumnSizingToContainer}
                            onMouseDown={header.getResizeHandler()}
                            onTouchStart={header.getResizeHandler()}
                          >
                            <GripVertical aria-hidden="true" className={cn('size-3.5', { 'text-ring': header.column.getIsResizing() })} />
                          </button>
                        ) : null}
                      </DataGridHead>
                    )
                  })}
                </DataGridRow>
              ))}
            </DataGridHeader>
            <DataGridBody>
              {isPending ? (
                <DataGridRow>
                  <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                    <div className="text-muted-foreground">Loading ${schema.label}...</div>
                  </DataGridCell>
                </DataGridRow>
              ) : error ? (
                <DataGridRow>
                  <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                    <div className="text-destructive">Error loading ${schema.label}: {error.message}</div>
                  </DataGridCell>
                </DataGridRow>
              ) : table.getRowModel().rows?.length ? (
                table.getRowModel().rows.map((row) => (
                  <DataGridRow
                    key={row.id}
                    className={cn('hover:bg-muted/50', {
                      'cursor-pointer': ${hasEdit ? 'true' : 'false'}
                    })}
                    data-state={row.getIsSelected() ? 'selected' : undefined}${
                      hasEdit
                        ? `
                    onClick={(e) => {
                      const target = e.target as HTMLElement
                      if (!e.currentTarget.contains(target)) return
                      if (target.closest('button, a, input, [role="checkbox"]')) return
                      router.push(\`${adminRoutePath}/${schema.name}/\${row.original.id}/edit\`)
                    }}`
                        : ''
                    }
                  >
                    {row.getVisibleCells().map((cell) => {
                    const meta = cell.column.columnDef.meta as Record<string, unknown> | undefined
                    return (
                        <DataGridCell
                          key={cell.id}
                          className={cn({
                            'text-center': meta?.align === 'center',
                            'text-right': meta?.align === 'right'
                          })}
                          data-column-id={cell.column.id}
                          onMouseEnter={() => setHoveredColumnId(cell.column.id)}
                          onMouseLeave={() => setHoveredColumnId((current) => (current === cell.column.id ? null : current))}
                        >
                          {flexRender(cell.column.columnDef.cell, cell.getContext())}
                        </DataGridCell>
                      )
                    })}
                  </DataGridRow>
                ))
              ) : (
                <DataGridRow>
                  <DataGridCell className="flex h-24 items-center justify-center text-center" style={{ gridColumn: '1 / -1' }}>
                    No ${schema.label} found.
                  </DataGridCell>
                </DataGridRow>
              )}
            </DataGridBody>
          </DataGrid>
        </CardContent>
      </Card>

      {shouldShowPagination ? (
        <DataTablePagination
          table={table}
          paginationMode="controlled"
          pageSize={safePageSize}
          pageIndex={safePageIndex}
          pageCount={pageCount}
          setPageIndex={(nextPageIndex) => setTableQuery({ page: nextPageIndex })}
          setPageSize={(nextPageSize) =>
            setTableQuery({ page: 0, size: nextPageSize })
          }
        />
      ) : null}
    </div>
  )
}
`

  return {
    files: [createGeneratedFile(`${pagesDir}/${schema.name}/${tableFileName}`, content)]
  }
}
